import StoreLocatorMaps from '@core_modules/storelocator/pages/default/components/Maps';
import SkeletonStoreLocator from '@core_modules/storelocator/pages/default/components/Skeleton';
import StoreList from '@core_modules/storelocator/pages/default/components/StoreList';
import Alert from '@common/Alert';
import { getGmapKey } from '@core/helpers/env';
import { useRouter } from 'next/router';
import { getCmsBlocks } from '@core_modules/cms/services/graphql';
import CmsRenderer from '@core_modules/cms/components/cms-renderer';
import Skeleton from '@common_skeleton';
import cx from 'classnames';

const StoreLocatorContent = ({
    gmapKey, storeLocations, t, isEngraving, storeUrlKey,
    storeConfig, handleGetMetadataStore,
}) => {
    // state
    const [centerPosition, setCenterPosition] = React.useState({});
    const [selectedStore, setSelectedStore] = React.useState();
    const [storeList, setStoreList] = React.useState(
        (storeLocations?.length > 0
            && storeLocations?.map((storeLocation) => ({
                ...storeLocation,
                lat: storeLocation.latitude,
                lng: storeLocation.longitude,
            }))) || null,
    );

    const widget_identifier = storeConfig?.widget_general_cms_block;
    const { data: dataCMS, loading, error } = getCmsBlocks({ identifiers: [widget_identifier] });

    // effect
    React.useEffect(() => {
        if (storeList !== null) {
            if (navigator && navigator.geolocation) {
                navigator.geolocation.getCurrentPosition((position) => {
                    const lng = position.coords.longitude;
                    const lat = position.coords.latitude;
                    setCenterPosition({ lat, lng });
                });
            } else {
                setCenterPosition({ lat: -6.17539, lng: 106.82715 });
            }
        }
    }, []);

    const router = useRouter();
    const storeIndex = storeList?.findIndex((item) => item?.rewrite_request_path === storeUrlKey);
    const handlePushParam = (value) => {
        router.push({
            query: {
                store: value,
            },
        });
    };

    if (storeList === null) {
        return (
            <div className="text-center">
                <Alert severity="error" className="capitalize">
                    {t('storelocator:noAvailableStore')}
                </Alert>
            </div>
        );
    }

    const filteredStoreList = isEngraving ? storeList.filter((item) => item.engraving) : storeList;

    React.useEffect(() => {
        if (storeUrlKey || storeIndex) {
            setSelectedStore(storeList[storeIndex]);
            const metadata = {
                meta_description: storeList[storeIndex]?.meta_description,
                meta_title: storeList[storeIndex]?.meta_title,
                meta_keywords: storeList[storeIndex]?.meta_keywords,
            };
            handleGetMetadataStore(metadata);
        }
    }, [storeIndex, storeUrlKey]);

    return (
        <>
            <div className="flex flex-row flex-wrap tablet:flex-nowrap gap-1">
                <div className="xs:basis-full sm:basis-4/12 md:basis-3/12 order-1 tablet:order-[0]">
                    <StoreList
                        t={t}
                        storeList={filteredStoreList}
                        totalAllStore={storeLocations.length}
                        onClickListItem={(store) => {
                            setCenterPosition({ lat: store.lat, lng: store.lng });
                            setSelectedStore(store);
                            handlePushParam(store.rewrite_request_path);
                            const metadata = {
                                meta_description: store?.meta_description,
                                meta_title: store?.meta_title,
                                meta_keywords: store?.meta_keywords,
                            };
                            handleGetMetadataStore(metadata);
                        }}
                        storeIndex={storeIndex}
                        storeUrlKey={storeUrlKey}
                    />
                </div>
                <div className="xs:basis-full xs:max-w-full sm:basis-8/12 md:basis-9/12">
                    <StoreLocatorMaps
                        t={t}
                        centerPosition={centerPosition}
                        mapPositions={storeList}
                        gmapKey={gmapKey}
                        setStoreList={setStoreList}
                        selectedStore={selectedStore}
                    />
                </div>
            </div>
            <div className="flex w-full">
                {loading && (
                    <Skeleton width="100%" height={40} className={cx('mb-4')} />
                )}
                { dataCMS?.cmsBlocks?.items?.length > 0 || !error ? (
                    <CmsRenderer content={dataCMS?.cmsBlocks?.items[0]?.content} storeConfig={storeConfig} />
                ) : null}
            </div>
        </>
    );
};

const StoreLocatorContentWrapper = (props) => {
    const {
        loading, storeLocations, t, isEngraving, storeUrlKey, storeConfig,
        handleGetMetadataStore,
    } = props;

    const GMAP_KEY = getGmapKey();
    return (
        <div className="mt-[12.5%] tablet:mt-[5%]">
            {loading || typeof window === 'undefined' ? (
                <SkeletonStoreLocator />
            ) : (
                <StoreLocatorContent
                    t={t}
                    gmapKey={GMAP_KEY}
                    storeLocations={storeLocations}
                    isEngraving={isEngraving}
                    storeUrlKey={storeUrlKey}
                    storeConfig={storeConfig}
                    handleGetMetadataStore={handleGetMetadataStore}
                />
            )}
        </div>
    );
};

export default StoreLocatorContentWrapper;
